// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import exportHotSheet from "@stanion/App/Handlers/HotSheet/ExportHotSheet";
import xGeneratePdf from "@stanion/App/Handlers/HotSheet/GeneratePdf";
import { xCommonLoadingOverlay } from "@stanion/App/Handlers/XNG/Common";
import { HotSheetContext } from "@stanion/App/Pages/HotSheetPage";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { xIsAuthenticated } from "@stanion/App/Store/Selectors/GeneralSelectors";
import { getSystemColor } from "@stanion/XDL/Utilities";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    xCommonLoadingOverlay,
    xGeneratePdf,
    exportHotSheet,
};

interface OwnProps extends WidgetProps {}
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ConduitHotSheetActionsStyles {
    buttonsWrapper?: InjectableCss;
    downloadPdf?: ButtonPresentationProps;
    downloadExcel?: ButtonPresentationProps;
}

export const conduitHotSheetActionsStyles: ConduitHotSheetActionsStyles = {};

const styles = conduitHotSheetActionsStyles;

const pdfHtml = (content: string) => `
        <html>
            <head>
                <style>
                    .hot-sheet-page {
                        padding: 40px 40px 0 40px;
                        font-family: "Open Sans";
                    }
                    
                    .hot-sheet-page .actions {
                        display: none;
                    }
                    
                    .hot-sheet-page .tables > div {
                        display: flex;
                        flex-wrap: wrap;
                        column-gap: 20px;
                        row-gap: 40px;
                        width: 100%;
                    }
                    .hot-sheet-page .tables > div > div {
                        flex: 0 0 calc(100% / 2 - 20px);
                    }

                    .hot-sheet-page table {
                        width: 100%;
                        border-collapse: collapse;
                    }

                    .hot-sheet-page thead {
                        background: ${getSystemColor("primary")};
                    }

                    .hot-sheet-page th {
                        background: ${getSystemColor("primary")};
                        padding: 8px 16px;
                        height: initial;
                        color: ${getSystemColor("common.white")};
                        font-size: 12px;
                        line-height: 20px;
                        text-align: start;
                        width: 50%;
                    }

                    .hot-sheet-page table tr {
                        border-bottom: 1px solid ${getSystemColor("common.border")};
                    }

                    .hot-sheet-page td {
                        padding: 8px 16px;
                        height: initial;
                    }

                    .hot-sheet-page td a, .hot-sheet-page td span { 
                        text-decoration: none;
                        font-weight: bold;
                        font-size: 14px;
                        line-height: 22px;
                    }
                    .hot-sheet-page td a {
                        color: ${getSystemColor("text.link")};
                    }
                    .hot-sheet-page tr:nth-child(even) {
                        background-color: ${getSystemColor("common.ui2")};
                    }
                </style>
            </head>

            <body>
                ${content}
            </body>
        </html>

`;
const ConduitHotSheetActions = ({
    isAuthenticated,
    parentId,
    xGeneratePdf,
    xCommonLoadingOverlay,
    exportHotSheet,
}: Props) => {
    const { views } = useContext(HotSheetContext);

    if (!isAuthenticated) {
        return null;
    }

    const downloadPDf = () => {
        const element = document.getElementById(parentId);
        if (element) {
            xCommonLoadingOverlay({ key: "add", value: "downloadPDf" });

            xGeneratePdf({
                html: pdfHtml(element.outerHTML),
                onSuccess: result => {
                    const blob = new Blob([result], { type: "application/pdf" });
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `Hot_Sheet_${new Date().toISOString()}.pdf`;
                    link.click();
                    xCommonLoadingOverlay({ key: "clear", value: "downloadPDf" });
                },
            });
        }
    };

    const downloadExcel = () => {
        if (views) {
            exportHotSheet({ views });
        }
    };

    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            <Button onClick={downloadPDf} {...styles.downloadPdf}>
                {translate("Download PDF")}
            </Button>
            <Button onClick={downloadExcel} {...styles.downloadExcel}>
                {translate("Download Excel")}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ConduitHotSheetActions),
    definition: {
        group: "Hot Sheet" as any,
        displayName: "Hot Sheet Actions",
        allowedContexts: ["HotSheetPage"] as any,
    },
};

export default widgetModule;
